/**
 * Chat-left route classifier for the focus-pane invariant
 * (`ai-docs/lexide-agent-channel-tabs-impl-plan.md` §5.6).
 *
 * The focus-pane model puts a working surface on the RIGHT and the chat
 * conversation on the LEFT — but nothing enforced that the left actually WAS
 * the chat, so opening a pane over a full-page route (e.g. `/agent`) squeezed
 * that page into the left column. `FocusPaneHost` uses these helpers to enforce
 * the invariant: a focus pane is only ever open beside a chat-left route.
 *
 * Pure + route-only so it is unit-tested without a Router mount.
 */
import type { ActiveSelection } from "@/stores/useActiveContextStore";

/**
 * Does this route render the chat conversation in its left column (so a focus
 * pane may open beside it)? Everything else is a full-page destination that
 * owns the whole viewport.
 *
 * Chat-left: `/chat` (the global thread) and a case workspace
 * (`/cases/:caseId/*` and the long-URL
 * `/clients/:c/matters/:m/cases/:caseId/*`). NOT the `/cases` list or the
 * recycle bin — those are full-page.
 *
 * `/home` is DELIBERATELY NOT chat-left (operator direction 2026-07-22). It is
 * always the check-in dashboard — greeting, status card, recent-case tiles —
 * never a transcript ("Lexiun has exactly one global thread… there are no 'new'
 * chats, ever", `HomeLayout`). Treating it as chat-left is what put the WELCOME
 * SCREEN in the left column beside Contacts/Pendências instead of a
 * conversation. Excluding it makes the invariant navigate to `/chat`, which is
 * the actual global conversation.
 */
export function isChatLeftRoute(pathname: string): boolean {
  // Normalize a trailing slash so `/home/` matches `/home`.
  const p = pathname.replace(/\/+$/, "") || "/";

  // `/home` is the check-in dashboard, not a conversation — see the note above.
  if (p === "/chat") return true;

  // Long-URL case workspace.
  if (/^\/clients\/[^/]+\/matters\/[^/]+\/cases\/[^/]+(\/|$)/.test(p)) return true;

  // Short-URL case workspace — but the list and the recycle bin are full-page.
  if (p === "/cases" || p === "/cases/recycle-bin") return false;
  if (/^\/cases\/[^/]+(\/|$)/.test(p)) return true;

  return false;
}

/**
 * Where to send the LEFT column when a pane opens over a full-page route: the
 * active case's workspace when one is selected, else the GLOBAL CONVERSATION.
 *
 * With no case selected this returns `/chat`, not `/home` (operator direction
 * 2026-07-22): the lawyer must be able to talk to the concierge beside the
 * surface, and `/home` is a dashboard with no transcript. This helper is shared
 * by every focus-pane surface — contacts, pendências, browser, agent — so the
 * correction applies to all of them at once.
 *
 * The literal `/cases/${caseId}` is the established idiom (there is no
 * route-builder helper in the codebase; `CaseRouteResolver` resolves the parents
 * server-side).
 */
export function chatTargetForContext(selection: ActiveSelection | null): string {
  if (selection?.caseId) return `/cases/${selection.caseId}`;
  return "/chat";
}
